import { z } from "zod";

const NumericalScoreValueSchema = z.number().nullable();
const JsonScoreValueSchema = z.record(z.string(), z.unknown()).nullable();

const ScoreValueSchema = NumericalScoreValueSchema.or(JsonScoreValueSchema);

export const ScoreSchema = z.object({
    value: ScoreValueSchema,
    avg: ScoreValueSchema.optional(),
    min: ScoreValueSchema.optional(),
    max: ScoreValueSchema.optional(),
    median: ScoreValueSchema.optional(),
    sum: ScoreValueSchema.optional(),
    count: ScoreValueSchema.optional(),
});

const DimensionValueSchema = z.object({
    id: z.string(),
    score: ScoreSchema,
    label: z.string(),
    get dimensions() {
        return DimensionSchema.array();
    },
});

export const VariationSchema = z.object({
    id: z.int().nullable(),
    unit: z.string(),
    label: z.string(),
    defaultDisplay: z.boolean(),
    score: ScoreSchema.optional(),
});

const DimensionSchema = z.object({
    label: z.string(),
    values: DimensionValueSchema.array(),
});

export const MainScoreSchema = z.object({
    score: ScoreSchema,
    unit: z.string(),
    label: z.string(),
    type: z.string(),
    displayMode: z.string(),
    defaultDisplay: z.boolean(),
    dimensions: DimensionSchema.array(),
    variations: VariationSchema.array(),
    get subscores() {
        return OlapScoresResultSchema;
    },
});

export const OlapScoresResultSchema = z.record(z.string(), MainScoreSchema);

/**
 * Per-supplier "where does this score come from?" payload, mirroring the cascade
 * implemented backend-side in `WeightedAverageHelper::getIngredientPostsScoreWeights`.
 *
 * Source kinds (must stay in sync with `IngredientScoreSourceMetaResolver` PHP types):
 *   - `supplier_reference`  → IngredientSupplier.ingredientReference carries scores for the campaign
 *   - `ssq`                 → a SsqPost exists for this (ingredient, supplier, campaign)
 *   - `ingredient_reference`→ no supplier path; falls back to a platform/tenant-owned reference
 *   - `sure`                → no supplier path; falls back to the Sure reference (FoodPilot-managed)
 *   - `none`                → no scores at all
 *
 * The frontend uses this to render the `ScoreSourceChip` badge on each cell
 * without having to re-derive the cascade rules client-side. Only `supplier_reference`,
 * `ingredient_reference`, and `sure` are visually badged today (cf. plan §FE-21);
 * `ssq` is intentionally chip-less because that's the "default" path we don't want to
 * decorate. `none` shouldn't be reached on a renderable score but is exposed for
 * completeness.
 */
export const ScoreSourceKindSchema = z.enum(["supplier_reference", "ssq", "ingredient_reference", "sure", "none"]);
export const ScoreSourceOwnerSchema = z.object({
    id: z.number(),
    name: z.string(),
    avatarUrl: z.string().nullable(),
});
export const ScoreSourceMetaSchema = z.object({
    source: ScoreSourceKindSchema,
    owner: ScoreSourceOwnerSchema.optional(),
});
// `bySupplier` is a (companyId → SourceMeta) map. The PHP API serializes empty
// arrays as JSON `[]`, so we accept either shape and coerce the empty-array
// case to an empty record. The backend now always emits an object via
// `ArrayObject`, but keeping this tolerant prevents a hard regression on
// cached / older responses.
const BySupplierSchema = z
    .union([z.record(z.string(), ScoreSourceMetaSchema), z.array(z.never()).length(0)])
    .transform((value): Record<string, z.infer<typeof ScoreSourceMetaSchema>> => (Array.isArray(value) ? {} : value));

export const IngredientScoreSourceMetaPayloadSchema = z.object({
    bySupplier: BySupplierSchema,
    fallback: ScoreSourceMetaSchema,
});

const TransportStageContributionSchema = z.object({
    stage: z.string(),
    source: z.string(),
    originCountryCode: z.string().nullable(),
    destinationCountryCode: z.string().nullable().optional(),
    circuitName: z.string().nullable(),
    totalDistanceKm: z.number().nullable().optional(),
    legs: z
        .object({
            name: z.string().nullable(),
            transportModeId: z.number().nullable(),
            distanceKm: z.number().nullable(),
            isRefrigerated: z.boolean(),
            originCountryCode: z.string().nullable(),
            destinationCountryCode: z.string().nullable(),
        })
        .array()
        .optional(),
});

export const TransportScoreContributionSchema = z.object({
    value: z.number().nullable(),
    unit: z.string().nullable(),
    source: z.string(),
    status: z.enum(["calculated", "unavailable"]),
    stages: TransportStageContributionSchema.array(),
});

export const TransportScoresPayloadSchema = z.object({
    basis: z.string(),
    byScoreId: z.record(z.string(), TransportScoreContributionSchema),
});

export const OlapScoresSchema = z.object({
    olapScores: OlapScoresResultSchema,
    scoreSourceMeta: IngredientScoreSourceMetaPayloadSchema.optional(),
    transportScores: TransportScoresPayloadSchema.optional(),
});

export const OlapScoresWithPrevScoresSchema = z.object({
    olapScores: OlapScoresResultSchema,
    olapPrevScores: OlapScoresResultSchema.optional(),
    prevCampaignName: z.string().optional(),
    scoreSourceMeta: IngredientScoreSourceMetaPayloadSchema.optional(),
    transportScores: TransportScoresPayloadSchema.optional(),
});

export type NumericalScoreValue = z.infer<typeof NumericalScoreValueSchema>;
export type JsonScoreValue = z.infer<typeof JsonScoreValueSchema>;
export type ScoreValue = z.infer<typeof ScoreValueSchema>;
export type Score = z.infer<typeof ScoreSchema>;
export type MainScore = z.infer<typeof MainScoreSchema>;
export type Variation = z.infer<typeof VariationSchema>;
export type Dimension = z.infer<typeof DimensionSchema>;
export type DimensionValue = z.infer<typeof DimensionValueSchema>;
export type OlapScoresResult = z.infer<typeof OlapScoresResultSchema>;

export type OlapScores = z.infer<typeof OlapScoresSchema>;
export type OlapScoresWithPrevScores = z.infer<typeof OlapScoresWithPrevScoresSchema>;

export type ScoreSourceKind = z.infer<typeof ScoreSourceKindSchema>;
export type ScoreSourceOwner = z.infer<typeof ScoreSourceOwnerSchema>;
export type ScoreSourceMeta = z.infer<typeof ScoreSourceMetaSchema>;
export type IngredientScoreSourceMetaPayload = z.infer<typeof IngredientScoreSourceMetaPayloadSchema>;
export type TransportScoreContribution = z.infer<typeof TransportScoreContributionSchema>;
export type TransportScoresPayload = z.infer<typeof TransportScoresPayloadSchema>;

/**
 * Resolve which `ScoreSourceMeta` should label the *aggregate* row score for
 * an ingredient — single-source flavour, kept for cell-level chips and legacy
 * call sites that don't enumerate contributors.
 *
 * Business rule (mirrors the OLAP cascade in
 * `WeightedAverageHelper::getIngredientPostsScoreWeights`):
 *   1. If at least one supplier contributes through a SsqPost, the aggregate
 *      score is "precise" questionnaire data — render no chip.
 *   2. If at least one supplier contributes through its own `IngredientReference`
 *      (`supplier_reference`), surface that as the aggregate source. When
 *      several suppliers point to the *same* owner we keep that owner on the
 *      meta, otherwise we surface a generic supplier-reference badge.
 *   3. Otherwise (every supplier fell back to the ingredient-level reference,
 *      or no supplier at all), use the ingredient-level `fallback` descriptor.
 *
 * Returning `null` means: do not render a chip for this row.
 *
 * For the *aggregate row* badge the FE should prefer
 * {@link resolveRowContributors} which enumerates every distinct contributor
 * so the tooltip can spell them all out (cf. Apr-25 product feedback on the
 * "Eurial vs Valrhona" / mixed-sources confusion).
 */
export const resolveRowSourceMeta = (
    payload: IngredientScoreSourceMetaPayload | undefined | null,
): ScoreSourceMeta | null => {
    if (!payload) return null;

    const suppliers = Object.values(payload.bySupplier);

    if (suppliers.some((meta) => meta.source === "ssq")) {
        return null;
    }

    const supplierReferenceContributors = suppliers.filter((meta) => meta.source === "supplier_reference");

    if (supplierReferenceContributors.length > 0) {
        const firstOwner = supplierReferenceContributors[0].owner;
        const allSameOwner = supplierReferenceContributors.every((meta) => meta.owner?.id === firstOwner?.id);

        return allSameOwner && firstOwner ?
                { source: "supplier_reference", owner: firstOwner }
            :   { source: "supplier_reference" };
    }

    return payload.fallback;
};

/**
 * Enumerate the *distinct* score-source contributors that fed the aggregate
 * row. Unlike {@link resolveRowSourceMeta} which collapses everything into a
 * single descriptor, this returns one entry per unique `(source, owner)`
 * tuple so the chip tooltip can spell every contributor out — e.g.:
 *
 *   "Score basé sur la référence fournisseur Valrhona et la référence plateforme."
 *   "Certains scores proviennent d'un questionnaire, d'autres de la référence
 *    fournisseur Valrhona et de la référence Sure."
 *
 * Rules:
 *   - Each `bySupplier` entry contributes its own `(source, owner.id)` key.
 *   - SSQ contributions ARE included now (they used to short-circuit the
 *     aggregate badge to `null`). The chip wrapper decides whether a pure-SSQ
 *     row stays chip-less; when SSQ is mixed with reference-backed suppliers
 *     the user wants both surfaced (see Apr-25 product feedback).
 *   - When no supplier surfaces (empty payload), fall back to the
 *     ingredient-level `fallback` descriptor.
 *   - `none` entries are filtered out — they signal "no contribution" and
 *     have no UX value.
 *
 * The output order is the suppliers' insertion order, then the fallback (when
 * used). That order is stable because PHP `ArrayObject` round-trips through
 * JSON as an ordered object.
 */
export const resolveRowContributors = (
    payload: IngredientScoreSourceMetaPayload | undefined | null,
): ScoreSourceMeta[] => {
    if (!payload) return [];

    const seen = new Map<string, ScoreSourceMeta>();
    const pushIfNew = (meta: ScoreSourceMeta): void => {
        if (meta.source === "none") return;
        const key = `${meta.source}::${meta.owner?.id ?? "_"}`;
        if (!seen.has(key)) seen.set(key, meta);
    };

    for (const meta of Object.values(payload.bySupplier)) {
        pushIfNew(meta);
    }

    if (seen.size === 0) {
        pushIfNew(payload.fallback);
    }

    return Array.from(seen.values());
};
